import classNames from 'classnames'
import React, { ReactNode } from 'react'

import Data from 'data'

import './ItemLibrary.scss'

import { ItemDefinition, PassiveSkillDefinition } from '@/data/types'
import { LibraryNode } from '@/library'
import { gemItemIcon, gemName } from '@/logic/gem'
import { TreePicker } from '@/tree/TreeViewer'
import { RawTooltip } from '@/ui/tooltip'
import { faChevronDown, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SmallIcon } from 'components/Icon'
import { itemBaseIcon, itemGetBaseColor } from 'logic/item'
import { PassiveSkillTooltip, WithGemTemplateTooltip, WithItemTemplateTooltip, WithPassiveTooltip } from 'tooltip'
import Resizable from 'ui/resizable'
import Select, { underlinerFunc } from 'ui/select'
import { VirtualList } from 'ui/virtual'
import { searchRegex } from 'utils'

export type LibraryClickHandler = (
  e: React.MouseEvent | undefined,
  id: string,
  type: 'item' | 'passive' | 'atlas' | 'skill' | 'tattoo' | 'gem',
  iconOnly: boolean,
  qualityType: number
) => void

export function ItemCategory({
  category,
  root,
  active,
  setActive
}: {
  category: LibraryNode
  root?: boolean
  active: LibraryNode
  setActive: (node: LibraryNode) => void
}) {
  const [expanded, setExpanded] = React.useState(category.expanded ?? false)
  const onToggle = React.useCallback(() => setExpanded((e) => !e), [])
  const onActivate = React.useCallback(() => setActive(category), [setActive, category])
  return (
    <li className={classNames({ 'poe-expanded': expanded || root })}>
      <div
        className={classNames('poe-category-header', {
          'poe-active': active === category
        })}>
        <span onClick={onActivate} onDoubleClick={onToggle}>
          {category.name}
        </span>
        {!!(!root && category.children) && (
          <FontAwesomeIcon icon={faChevronDown} className="poe-category-toggle" onClick={onToggle} />
        )}
      </div>
      {category.children && (
        <ul className="poe-category-list">
          {category.children.map((item, index) => (
            <ItemCategory key={index} category={item} active={active} setActive={setActive} />
          ))}
        </ul>
      )}
    </li>
  )
}

function TattooTooltip({ id }: { id: string }) {
  const tattoo = Data.tattoos[id]
  if (tattoo.allocatedSkill) {
    return <PassiveSkillTooltip skill={Data.passive_skills[tattoo.allocatedSkill]} />
  }
  if (!tattoo.name) return null
  const skill: PassiveSkillDefinition = {
    name: tattoo.name,
    icon: tattoo.icon ?? 'No icon',
    stats: tattoo.stats,
    tattoo: id
  }
  return <PassiveSkillTooltip skill={skill} />
}

type LibraryRenderer = (items: string[], node: LibraryNode, search: RegExp | undefined) => ReactNode

export default function ItemLibrary({
  root,
  onClick,
  hideTree,
  iconBox,
  customBox,
  uniques,
  selectMapTier,
  renderer
}: {
  root: LibraryNode
  onClick: LibraryClickHandler
  hideTree?: boolean
  iconBox?: boolean
  customBox?: string
  uniques?: boolean
  selectMapTier?: boolean
  renderer?: LibraryRenderer
}) {
  const [search, setSearch] = React.useState('')
  const onSetSearch = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), [])
  const onClearSearch = React.useCallback(() => setSearch(''), [])
  const [iconOnly, setIconOnly] = React.useState(false)
  const [transfigured, setTransfigured] = React.useState(0)
  const onClickIconOnly = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setIconOnly(!!e.target.checked),
    []
  )
  const onSetTransfigured = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setTransfigured(parseInt(e.target.value)),
    []
  )

  const [active, setActive] = React.useState(root)
  const onSetActive = React.useCallback(
    (value: LibraryNode) => {
      setActive(value)
      onClearSearch()
    },
    [onClearSearch]
  )

  const type = active.type ?? 'item'

  const itemsRaw = React.useMemo<string[]>(() => {
    switch (type) {
      case 'item': {
        const result = Object.keys(Data.items).filter((id) => Data.items[id].name && active.filter(Data.items[id]))
        if (uniques)
          result.push(
            ...Object.keys(Data.uniques).filter((id) =>
              active.filter(Data.items[Data.uniques[id].variants?.[0]?.base], true)
            )
          )
        return result.sort((a, b) => {
          const ia = Data.items[a] || Data.uniques[a],
            ib = Data.items[b] || Data.uniques[b]
          return ia.name.localeCompare(ib.name)
        })
      }
      case 'passive': {
        const result = Object.keys(Data.passive_skills).filter(
          (id) => Data.passive_skills[id].name && active.filter(Data.passive_skills[id])
        )
        return result.sort((a, b) => {
          const ia = Data.passive_skills[a],
            ib = Data.passive_skills[b]
          return ia.name.localeCompare(ib.name)
        })
      }
      case 'gem': {
        const result = Object.keys(Data.gems).filter((id) => active.filter(Data.gems[id], id))
        return result.sort((a, b) => {
          const ia = gemName(Data.gems[a]),
            ib = gemName(Data.gems[b])
          return ia.localeCompare(ib)
        })
      }
      case 'skill': {
        const result = Object.keys(Data.gems).filter(
          (id) => Data.gems[id].active_skill?.display_name && active.filter(Data.gems[id])
        )
        return result.sort((a, b) => {
          const ia = Data.gems[a].active_skill,
            ib = Data.gems[b].active_skill
          return ia!.display_name.localeCompare(ib!.display_name)
        })
      }
      case 'tattoo': {
        const result = Object.keys(Data.tattoos).filter(
          (id) => (Data.tattoos[id].name || Data.tattoos[id].allocatedSkill) && active.filter(Data.tattoos[id])
        )
        return result.sort((a, b) => {
          const ta = Data.tattoos[a]
          const tb = Data.tattoos[b]
          return (ta.name ?? Data.passive_skills[ta.allocatedSkill!].name).localeCompare(
            tb.name ?? Data.passive_skills[tb.allocatedSkill!].name
          )
        })
      }
    }
  }, [active, uniques, type])

  const searchRe = React.useMemo(() => searchRegex(search, true), [search])

  const items = React.useMemo(() => {
    if (!searchRe) return itemsRaw
    switch (type) {
      case 'item':
        return itemsRaw.filter((id) => {
          const item = Data.items[id] ? Data.items[id] : Data.uniques[id]
          let name = item.name
          if ('skills' in item && item.skills) {
            const skill = Data.gems[item.skills[transfigured] ?? item.skills[0]]
            name = skill.active_skill?.display_name ?? name
          }
          if (searchRe && !name.match(searchRe)) return false
          return true
        })
      case 'passive':
        return itemsRaw.filter((id) => {
          const item = Data.passive_skills[id]
          if (searchRe && !item.name.match(searchRe)) return false
          return true
        })
      case 'gem':
        return itemsRaw.filter((id) => {
          const item = Data.gems[id]
          if (searchRe && !gemName(item).match(searchRe)) return false
          return true
        })
      case 'skill':
        return itemsRaw.filter((id) => {
          const item = Data.gems[id].active_skill!
          if (searchRe && !item.display_name.match(searchRe)) return false
          return true
        })
      case 'tattoo':
        return itemsRaw.filter((id) => {
          const item = Data.tattoos[id]
          const name = item.name ?? Data.passive_skills[item.allocatedSkill!].name
          if (searchRe && !name.match(searchRe)) return false
          return true
        })
    }
  }, [searchRe, itemsRaw, type, transfigured])

  const renderItem = React.useCallback(
    (id: string) => {
      if (type === 'item') {
        const item = Data.items[id] ? Data.items[id] : Data.uniques[id]
        if (!item) return null
        const color = Data.uniques[id] ? 'unique' : itemGetBaseColor(item as ItemDefinition) || 'normal'
        let name = item.name
        let icon = itemBaseIcon(item, transfigured || undefined)
        if ('skills' in item && item.skills) {
          const skill = Data.gems[item.skills[transfigured] ?? item.skills[0]]
          name = skill.active_skill?.display_name ?? name
          icon = gemItemIcon(skill) ?? icon
        }
        return (
          <li key={id}>
            <WithItemTemplateTooltip
              name={id}
              variant={
                'skills' in item && item.skills
                  ? transfigured
                  : selectMapTier && 'item_class' in item && item.item_class === 'Map'
                  ? transfigured || undefined
                  : undefined
              }>
              <span onClick={(e) => onClick?.(e, id, type, iconOnly, transfigured)} className={`poe-item-${color}`}>
                {!!icon && <SmallIcon icon={icon} />}
                {underlinerFunc(name, searchRe)}
              </span>
            </WithItemTemplateTooltip>
          </li>
        )
      }
      if (type === 'passive') {
        const item = Data.passive_skills[id]
        if (!item) return null
        return (
          <li key={id}>
            <WithPassiveTooltip id={id}>
              <span onClick={(e) => onClick?.(e, id, type, iconOnly, transfigured)}>
                {!!item.icon && <SmallIcon icon={item.icon} className="poe-passive-icon" />}
                {underlinerFunc(item.name, searchRe)}
              </span>
            </WithPassiveTooltip>
          </li>
        )
      }
      if (type === 'gem') {
        const item = Data.gems[id]
        if (!item) return null
        const icon = gemItemIcon(item)
        return (
          <li key={id}>
            <WithGemTemplateTooltip id={id}>
              <span onClick={(e) => onClick?.(e, id, type, iconOnly, 0)}>
                {icon && <SmallIcon icon={icon} />}
                {underlinerFunc(gemName(item), searchRe)}
              </span>
            </WithGemTemplateTooltip>
          </li>
        )
      }
      if (type === 'skill') {
        const item = Data.gems[id]
        if (!item) return null
        return (
          <li key={id}>
            <WithGemTemplateTooltip id={id}>
              <span onClick={(e) => onClick?.(e, id, type, iconOnly, 0)}>
                {!!item.active_skill!.icon && <SmallIcon icon={item.active_skill!.icon} />}
                {underlinerFunc(item.active_skill!.display_name, searchRe)}
              </span>
            </WithGemTemplateTooltip>
          </li>
        )
      }
      if (type === 'tattoo') {
        const item = Data.tattoos[id]
        if (!item) return null
        const name = item.name ?? Data.passive_skills[item.allocatedSkill!].name
        const icon = item.icon ?? Data.passive_skills[item.allocatedSkill!].icon
        return (
          <li key={id}>
            <RawTooltip tooltip={<TattooTooltip id={id} />}>
              <span onClick={(e) => onClick?.(e, id, type, iconOnly, 0)}>
                <SmallIcon icon={icon} className="poe-passive-icon" />
                {underlinerFunc(name, searchRe)}
              </span>
            </RawTooltip>
          </li>
        )
      }
    },
    [searchRe, onClick, iconOnly, type, transfigured, selectMapTier]
  )

  const onClickTree = React.useCallback(
    (id: number, e?: React.MouseEvent) =>
      onClick?.(e, id.toString(), active.tree?.atlas ? 'atlas' : 'passive', false, 0),
    [onClick, active]
  )

  return (
    <div className="poe-ItemLibrary">
      {!hideTree && (
        <Resizable className="poe-ItemCategories" side="e">
          <ul>
            <ItemCategory category={root} root={true} active={active} setActive={onSetActive} />
          </ul>
        </Resizable>
      )}
      <div className="poe-ItemList">
        <div className="poe-library-filters">
          {selectMapTier && (
            <Select
              value={{
                id: transfigured,
                name: transfigured ? `Tier ${transfigured}` : 'Default'
              }}
              options={[...Array(18)].map((_, id) => ({
                id,
                name: id ? `Tier ${id}` : 'Default'
              }))}
              onChange={(opt) => opt && setTransfigured(opt.id)}
            />
          )}
          <div className="poe-library-search">
            <input placeholder="Search..." value={search} onChange={onSetSearch} />
            {!!search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="poe-clear" onClick={onClearSearch} />}
          </div>
        </div>
        {!!(iconBox || customBox) && (
          <>
            <label className="poe-icon-only">
              <input type="checkbox" checked={iconOnly} onChange={onClickIconOnly} /> {customBox || 'Icon Only'}
            </label>
            {!customBox && !selectMapTier && (
              <select className="poe-quality-type" value={transfigured} onChange={onSetTransfigured}>
                {['Normal', 'Transfigured 1', 'Transfigured 2'].map((name, id) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
        {active.tree ? (
          <TreePicker {...active.tree} search={search} onSelect={onClickTree} />
        ) : items.length ? (
          renderer ? (
            renderer(items, active, searchRe)
          ) : (
            <VirtualList
              maxHeight={1000}
              itemHeight={22}
              items={items}
              renderItem={renderItem}
              className="poe-item-list"
            />
          )
        ) : (
          <ul className="poe-item-list">
            <li className="poe-disabled">No matching items</li>
          </ul>
        )}
      </div>
    </div>
  )
}
